import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// A small reading timer for a book's page (a novel's journal, a fic's page).
// It's the same reading timer as on the study page — same bank, same running
// session — so starting it here or there is the same thing. Starting it here
// records this book as what you're reading.
//
// <ReadingTimer kind="novel" id={novelId} title={novel.title} />

const hm = (sec) => {
  const s = Math.max(0, Math.round(sec || 0));
  if (s > 0 && s < 60) return 'under a minute';
  const m = Math.ceil(s / 60);
  const h = Math.floor(m / 60);
  return h ? (m % 60 ? `${h}h ${m % 60}m` : `${h}h`) : `${m}m`;
};
// minutes only on a book's page, so nothing ticks every second while you read
// (the study page keeps the seconds): time read rounds down, time left rounds up
const readSoFar = (ms) => {
  const m = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(m / 60);
  return h ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
};
// a focus session running on the study page (it keeps its timer in this browser)
const focusing = () => {
  try {
    const t = JSON.parse(localStorage.getItem('studyTimer') || 'null');
    return !!(t && t.phase === 'focus' && t.remaining == null && t.endAt > Date.now());
  } catch (e) { return false; }
};

const BookIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#c9935a" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 6.5C10.3 5.2 7.8 4.5 4 4.5v13c3.8 0 6.3.7 8 2 1.7-1.3 4.2-2 8-2v-13c-3.8 0-6.3.7-8 2z" />
    <path d="M12 6.5v13" />
  </svg>
);

export default function ReadingTimer({ kind, id, title, onChange }) {
  const token = localStorage.getItem('token');
  const [state, setState] = useState(null);      // { visible, balance_seconds, active, offset }
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const stoppingRef = useRef(false);
  const noteTimer = useRef(null);

  const take = (data) => {
    if (!data) return;
    const serverNow = Date.parse(data.server_now);
    setState({
      visible: data.visible !== false,
      balance_seconds: data.balance_seconds || 0,
      active: data.active || null,
      offset: Number.isFinite(serverNow) ? serverNow - Date.now() : 0,
    });
    if (data.just_finished && data.just_finished.end_reason === 'ran_out') {
      say('your reading time is all used up — study to earn more 🌿', 7000);
    }
  };
  const say = (text, ms = 4500) => {
    setNote(text);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setNote(''), ms);
  };
  const load = () => axios.get(`${API}/reading/state?token=${token}`).then(res => take(res.data)).catch(() => {});

  useEffect(() => {
    load();
    const tick = setInterval(() => setNow(Date.now()), 1000);
    // catch up when you come back to the page (it may have run out, or been stopped on the study page)
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(tick); document.removeEventListener('visibilitychange', onVis); clearTimeout(noteTimer.current); };
  }, []);

  const active = state && state.active;
  const elapsed = active ? Math.max(0, now + state.offset - Date.parse(active.started_at)) : 0;
  const balanceMs = state ? state.balance_seconds * 1000 : 0;
  const leftMs = Math.max(0, balanceMs - elapsed);
  const sessionMs = Math.min(elapsed, balanceMs);
  const here = !!(active && active.item_kind === kind && active.item_id === id);

  const stop = (reason = 'stopped') => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    setBusy(true);
    axios.post(`${API}/reading/stop`, { token, reason })
      .then(res => {
        take(res.data);
        const done = res.data.just_finished;
        if (done && reason === 'stopped' && done.duration_seconds >= 60) say(`${hm(done.duration_seconds - (done.duration_seconds % 60))} of reading 📖`);
        if (onChange) onChange();
      })
      .catch(() => load())
      .finally(() => { setBusy(false); stoppingRef.current = false; });
  };

  // the balance never goes below zero: when it's used up, reading stops
  useEffect(() => {
    if (active && leftMs <= 0 && !stoppingRef.current) stop('ran_out');
  }, [now]);

  const start = () => {
    setBusy(true);
    axios.post(`${API}/reading/start`, { token, item_kind: kind, item_id: id })
      .then(res => { take(res.data); if (onChange) onChange(); })
      .catch(err => {
        const d = err?.response?.data?.detail;
        say(typeof d === 'string' ? d : "couldn't start — try again");
        load();
      })
      .finally(() => setBusy(false));
  };
  // reading something else right now: count it as this book instead
  const switchHere = () => {
    setBusy(true);
    axios.put(`${API}/reading/active-item`, { token, item_kind: kind, item_id: id })
      .then(res => { take(res.data); say(`now counting toward ${title || 'this'}`); if (onChange) onChange(); })
      .catch(() => load())
      .finally(() => setBusy(false));
  };

  if (!state || !state.visible) return null;

  return (
    <div style={{ ...s.bar, ...(here ? s.barOn : {}) }} role="region" aria-label="reading timer">
      <div style={s.row}>
        <span style={s.icon}><BookIcon /></span>
        {active ? (
          <span style={s.main}>
            <span style={s.clock}>{readSoFar(sessionMs)}</span>
            <span style={s.sub}>
              {here ? 'reading this' : `reading ${active.item_title || 'something else'}`} · {hm(leftMs / 1000)} left
            </span>
          </span>
        ) : (
          <span style={s.main}>
            <span style={s.left}>{hm(state.balance_seconds)} to read</span>
            {focusing() && <span style={s.sub}>yours to spend once your study session's done</span>}
          </span>
        )}
        <span style={s.actions}>
          {active ? (
            <>
              {!here && <button style={s.ghost} disabled={busy} onClick={switchHere}>count it here</button>}
              <button style={s.ghost} disabled={busy} onClick={() => stop('stopped')}>stop</button>
            </>
          ) : state.balance_seconds > 0 && !focusing() ? (
            <button style={s.start} disabled={busy} onClick={start}>start reading</button>
          ) : null}
        </span>
      </div>
      {note && <div style={s.note}>{note}</div>}
      {!active && state.balance_seconds <= 0 && !note && <div style={s.note}>every minute you study becomes a minute to read</div>}
    </div>
  );
}

const s = {
  bar: {
    background: '#17131b', border: '1px solid rgba(201,147,90,0.12)', borderRadius: '16px',
    padding: '10px 12px', marginBottom: '16px', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
  },
  barOn: { border: '1px solid rgba(201,147,90,0.32)', background: 'linear-gradient(180deg, #1e1825 0%, #17131b 100%)' },
  row: { display: 'flex', alignItems: 'center', gap: '10px' },
  icon: { width: '30px', height: '30px', borderRadius: '50%', background: 'rgba(201,147,90,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  main: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  clock: { fontSize: '17px', color: '#e8ddd0', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' },
  left: { fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', color: '#e8ddd0' },
  sub: { fontSize: '11.5px', color: '#8a7466', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  actions: { display: 'flex', gap: '6px', flexShrink: 0 },
  start: { padding: '7px 14px', borderRadius: '16px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 400 },
  ghost: { padding: '7px 12px', borderRadius: '16px', border: '1px solid rgba(201,147,90,0.25)', background: 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  note: { fontSize: '11.5px', color: '#8a7466', fontStyle: 'italic', marginTop: '7px', paddingLeft: '40px' },
};
